import React from 'react'
import { Link } from 'gatsby'
import { css, useTheme } from '@emotion/react'
import Container from 'components/container.js'
import ExternalLink from 'components/external-link'
import Socials from 'components/socials'

import logo from 'svgs/logo-rane-white.svg'
import accountIcon from 'svgs/account-icon-primary.svg'
import storeLogo from 'svgs/enginedj.svg'
import paths from 'data/paths.json'
import inmusicLogoBlack from 'svgs/inmusic-logo-black.svg'

const column = theme => css({
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    '& h6': {
        fontSize: 12,
        fontWeight: 600,
        color: theme.colors.black,
        fontStyle: 'normal',
        lineHeight: '100%',
    },
})

export const InMusic = (props) => {
    const { mobile } = props
    return(
        <Container id='inmusic' fluid css={{ backgroundColor: '#C2C2C2' }} {...props}>
            <Container>
                <div className='d-flex f-column' css={{
                    gap: mobile ? 20 : 48,
                    padding: mobile ? 20 : '40px 120px',
                    '@media(max-width: 768px)': { padding: mobile ? 20 : '40px 40px', },
                    '@media(max-width: 576px)': { padding: mobile ? 20 : '40px 16px', },
                }}>
                    {/* INMUSIC LOGO + LINE */}
                    <div className='d-flex a-center' css={{ gap: 24 }}>
                        <a href={paths.external.inmusic} target="_blank" rel="noreferrer">
                            <img src={inmusicLogoBlack} alt='inmusic logo black' height={32} />
                        </a>
                        <div style={{
                            height: 1,
                            flexGrow: 1,
                            backgroundColor: 'black',
                            transform: 'scaleY(0.5)', // half pixel
                        }} />
                    </div>

                    {/* COPYRIGHT | LINKS | PART OF... */}
                    <div className='d-flex j-between' css={{
                        '@media(max-width: 1024px)': {
                            flexDirection: 'column',
                            gap: 20,
                        }
                    }}>
                        <div className='d-flex f-wrap g-1' css={{ alignItems: 'baseline' }}>
                            <p className='xs black f-secondary'>
                                © {new Date().getFullYear()} inMusic, LLC. All Rights Reserved.
                            </p>
    
                            <div className='black'>
                                <Link to={paths.root.dealers.path} style={{ color: 'inherit' }}>
                                    Dealers
                                </Link>
                                &nbsp;|&nbsp;
                                <ExternalLink href={paths.external.support} style={{ color: 'inherit' }}>
                                    Support
                                </ExternalLink>
                                &nbsp;|&nbsp;
                                <ExternalLink href={paths.external.careers} style={{ color: 'inherit' }}>
                                    Careers
                                </ExternalLink>
                                &nbsp;|&nbsp;
                                <Link to={paths.root.privacy.path} style={{ color: 'inherit' }}>
                                    Privacy
                                </Link>
                                &nbsp;|&nbsp;
                                <Link to={paths.root.termsOfUse.path} style={{ color: 'inherit' }}>
                                    Terms of Use
                                </Link>
                                &nbsp;|&nbsp;
                                <div
                                    id="teconsent"
                                    className='d-inline-block xs'
                                    style={{ color: 'inherit' }}
                                />
                            </div>
                        </div>

                        <p className='sm bold black'>
                            Part of the inMusic family of brands
                        </p>
                    </div>
                </div>
            </Container>
        </Container>
    )
}

export default function Footer() {
    const theme = useTheme()
    return <footer id='footer'>
        {/* MAIN LINKS */}
        <Container fluid style={{ backgroundColor: '#14181F' }}>
            <Container>
                <div css={{
                    padding: '40px 120px 80px',
                    '@media(max-width: 768px)': {
                        padding: '40px 40px',
                    },
                    '@media(max-width: 576px)': {
                        padding: '40px 16px',
                    },
                }}>
                    {/* LOGO & SOCIALS */}
                    <div css={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 20,
                        '@media(max-width: 375px)': {
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                        }
                    }}>
                        <Link to={paths.root.path}>
                            <img src={logo} alt='' />
                        </Link>
                        <Socials />
                    </div>

                    {/* LINKS */}
                    <div css={{
                        display: 'grid',
                        gap: 20,
                        marginTop: 80,
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        '@media(max-width: 768px)': { gridTemplateColumns: 'repeat(2, 1fr)', gap: 40, marginTop: 40, },
                        '@media(max-width: 350px)': { gap: 20, marginTop: 20, }
                    }}>
                        <div css={column}>
                            <p className='xs fw-600'>Products</p>
                            <Link to={paths.root.products.controllers.path}>
                                Controllers
                            </Link>
                            <Link to={paths.root.products.mixers.path}>
                                Mixers
                            </Link>
                            <Link to={paths.root.products.legacy.path}>
                                Legacy
                            </Link>
                            <ExternalLink to={paths.external.enginedj} className='d-flex g-2'>
                                Engine DJ
                                <img src='/svg/external-link-white.svg' alt='' />
                            </ExternalLink>
                        </div>
    
                        <div css={column}>
                            <p className='xs fw-600'>Company</p>
                            <Link to={paths.root.about.path}>
                                About Us
                            </Link>
                            <Link to={paths.root.dealers.path}>
                                Dealers
                            </Link>
                        </div>
    
                        <div css={column}>
                            <p className='xs fw-600'>Support</p>
                            <Link to={paths.root.downloads.path}>
                                Downloads
                            </Link>
                            <ExternalLink href={paths.external.support} className='d-flex g-2'>
                                Contact Us
                                <img src='/svg/external-link-white.svg' alt='' />
                            </ExternalLink>
                            <ExternalLink href={paths.external.forum} className='d-flex g-2'>
                                Forum
                                <img src='/svg/external-link-white.svg' alt='' />
                            </ExternalLink>
                            <Link to={paths.root.repairs.path}>
                                Repairs
                            </Link>
                        </div>
                        
                        <div css={column}>
                            <p className='xs fw-600'>Account</p>
                            <a
                                href={paths.external.login}
                                target='_blank'
                                rel='noreferrer'
                                className='d-flex a-center g-2'
                                css={{ padding: 12, border:`1px solid #FFF` }}
                            >
                                <img src={accountIcon} alt='' height={16} width={16} />
                                Login
                            </a>
                        </div>
                    </div>
                </div>
            </Container>
        </Container>

        {/* ENGINE DJ */}
        <Container fluid style={{ background: 'linear-gradient(90deg, #8EE283 0%, #5DAFD6 100%)' }}>
            <Container>
                    <ExternalLink
                        href={paths.external.enginedj}
                        className='d-flex a-end j-between td-0'
                        css={{
                            gap: 20,
                            padding: '12px 120px',
                            '@media(max-width: 768px)': { padding: '12px 40px' },
                            '@media(max-width: 576px)': {
                                gap: 24,
                                padding: 12,
                                flexDirection: 'column',
                            },
                        }}
                    >
                        <img src={storeLogo} alt='' height={18} />
                        <span className='d-flex uppercase f-tertiary' style={{ gap: 5, color: '#000' }}>
                            <strong style={{ fontWeight: 800 }}>
                                DISCOVER ENGINE DJ,
                            </strong> now with Apple Music, Stems, lighting controls and much more
                            <img
                                src='/svg/external-link-black.svg'
                                alt=''
                                height={12}
                            />
                        </span>
                    </ExternalLink>
            </Container>
        </Container>

        <InMusic />
    </footer>
}
